import { useState, useEffect, useCallback, useRef } from 'react'
import { App } from 'antd'
import type { SurveyRecord, InterviewRecord } from '@bandos/shared'
import { MindmapSurveyService } from '../../features/survey/services/mindmapSurveyService'
import { MindmapInterviewService } from '../../features/interview/services/mindmapInterviewService'
import { DocumentService } from '../../services/documents/documentService'
import { ventureDocumentToUploadedItem, type UploadedResearchItem } from './UploadedResearchCard'

const POLL_INTERVAL_MS = 3000
const MAX_POLL_COUNT = 60 // ~3 minutes max polling (same ceiling as useVentureDocuments)

/**
 * Classifications that trigger AI summarization in the backend pipeline.
 * A 'ready' document with one of these classifications but no ai_document_type yet
 * means summarization is still in-flight — keep polling.
 */
const SUMMARIZABLE_CLASSIFICATIONS = new Set(['Survey data', 'Interview transcripts'])

function needsPolling(docs: UploadedResearchItem[]): boolean {
  return docs.some(
    (d) =>
      d.status === 'processing' ||
      (d.status === 'ready' &&
        d.content_classification !== null &&
        SUMMARIZABLE_CLASSIFICATIONS.has(d.content_classification) &&
        d.ai_document_type === null)
  )
}

interface UseResearchDataReturn {
  surveys: SurveyRecord[]
  interviews: InterviewRecord[]
  uploadedDocuments: UploadedResearchItem[]
  loading: boolean
  setSurveys: React.Dispatch<React.SetStateAction<SurveyRecord[]>>
  setInterviews: React.Dispatch<React.SetStateAction<InterviewRecord[]>>
  setUploadedDocuments: React.Dispatch<React.SetStateAction<UploadedResearchItem[]>>
}

export function useResearchData(mindmapId: string | undefined): UseResearchDataReturn {
  const { notification } = App.useApp()
  const [surveys, setSurveys] = useState<SurveyRecord[]>([])
  const [interviews, setInterviews] = useState<InterviewRecord[]>([])
  const [uploadedDocuments, setUploadedDocuments] = useState<UploadedResearchItem[]>([])
  const [loading, setLoading] = useState(true)
  const unmountedRef = useRef(false)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const pollCountRef = useRef(0)

  useEffect(() => {
    unmountedRef.current = false
    return () => {
      unmountedRef.current = true
    }
  }, [])

  // Fetch all research data on mount / mindmapId change
  useEffect(() => {
    if (!mindmapId) return
    let cancelled = false
    setLoading(true)

    void Promise.all([
      MindmapSurveyService.list(mindmapId),
      MindmapInterviewService.list(mindmapId),
      DocumentService.listDocumentsForMindmap(mindmapId).catch(
        (err: unknown): Error => (err instanceof Error ? err : new Error('Unknown error'))
      ),
    ])
      .then(([surveysRes, interviewsRes, docsRes]) => {
        if (cancelled) return
        if (surveysRes.error) {
          notification.error({
            title: 'Failed to load surveys',
            description: surveysRes.error.message,
          })
        } else {
          setSurveys(surveysRes.data?.surveys ?? [])
        }
        if (interviewsRes.error) {
          notification.error({
            title: 'Failed to load interviews',
            description: interviewsRes.error.message,
          })
        } else {
          setInterviews(interviewsRes.data?.interviews ?? [])
        }
        if (docsRes instanceof Error) {
          notification.error({
            title: 'Failed to load uploaded files',
            description: docsRes.message,
          })
        } else {
          setUploadedDocuments(docsRes.map(ventureDocumentToUploadedItem))
        }
      })
      .catch(() => {
        if (!cancelled) {
          notification.error({ title: 'Failed to load research data' })
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [mindmapId, notification])

  // Re-fetch documents periodically while any are still processing
  const refetchDocuments = useCallback(() => {
    if (!mindmapId) return
    void DocumentService.listDocumentsForMindmap(mindmapId)
      .then((docs) => {
        if (!unmountedRef.current) {
          setUploadedDocuments(docs.map(ventureDocumentToUploadedItem))
        }
      })
      .catch(() => {
        // Poll failures are silent — the user already sees "Analysing…" state
      })
  }, [mindmapId])

  useEffect(() => {
    const shouldPoll = needsPolling(uploadedDocuments) && pollCountRef.current < MAX_POLL_COUNT

    if (shouldPoll && mindmapId) {
      pollRef.current ??= setInterval(() => {
        pollCountRef.current++
        if (pollCountRef.current >= MAX_POLL_COUNT) {
          if (pollRef.current) {
            clearInterval(pollRef.current)
            pollRef.current = null
          }
          return
        }
        refetchDocuments()
      }, POLL_INTERVAL_MS)
    } else {
      if (pollRef.current) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
      if (!needsPolling(uploadedDocuments)) {
        pollCountRef.current = 0
      }
    }

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
    }
  }, [uploadedDocuments, mindmapId, refetchDocuments])

  return {
    surveys,
    setSurveys,
    interviews,
    setInterviews,
    uploadedDocuments,
    setUploadedDocuments,
    loading,
  }
}
